import { useEffect, useState } from 'react';
import screens from './Screens.module.css';
import styles from './TrackRecord.module.css';
import { Card, Segmented, Sparkline, Spinner, StatTile } from '../../components/ui';
import { AccuracyWidget } from '../../components/app/AccuracyWidget.jsx';
import { getPublicAccuracy, normaliseAccuracy, normaliseBySport } from '../../api/public.js';
import { useI18n } from '../../i18n/index.jsx';

/**
 * Public track record.
 *
 * Everything on this page is an accuracy claim, so figures are rendered only
 * when GET /api/public/accuracy returns them AND the sample clears
 * MIN_GRADED_FOR_PUBLIC_ACCURACY. Below the floor the widget shows the graded
 * count and the breakdown is withheld; with nothing graded, no numbers at all.
 *
 * The live payload carries only headline counts per window:
 *   {last_30_days|all_time: {graded, correct, incorrect, void, accuracy_pct}}
 *
 * The payload also carries `trend` (30 gap-free daily buckets, null on days
 * with nothing graded) and `by_sport` (all-time, per sport). The chart appears
 * once enough days carry data, and each sport row publishes a percentage only
 * once that sport clears its own floor — see the constants in api/public.js.
 *
 * Still missing: a 7-day window, and a previous-period delta.
 */
export function TrackRecord() {
  const { t } = useI18n();
  // The endpoint exposes two windows, last_30_days and all_time. There is no
  // 7-day bucket, so the range control offers only what the data supports.
  const [range, setRange] = useState('last_30_days');
  const [state, setState] = useState({ status: 'loading', data: null });

  useEffect(() => {
    document.title = `${t('track_title')} — Don Quack`;
  }, [t]);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading', data: null });

    getPublicAccuracy({ signal: controller.signal })
      .then((raw) => {
        const data = normaliseAccuracy(raw, range);
        setState({
          status: !data ? 'unavailable' : data.meetsFloor ? 'ready' : 'building',
          data,
          bySport: normaliseBySport(raw?.by_sport),
        });
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setState({ status: 'unavailable', data: null });
      });

    return () => controller.abort();
  }, [range]);

  const { status, data, bySport } = state;

  return (
    <section>
      <h2 className={screens.title}>{t('track_title')}</h2>
      <p className={screens.sub}>{t('track_subtitle')}</p>

      <div className={screens.toolbar}>
        <Segmented
          size="sm"
          options={[
            { value: 'last_30_days', label: t('range_30d') },
            { value: 'all_time', label: t('range_all') },
          ]}
          value={range}
          onChange={setRange}
          ariaLabel={t('track_over_time')}
        />
      </div>

      <AccuracyWidget variant="hero" window={range} sparkline={false} className={screens.blockGap} />

      {status === 'loading' && (
        <div className={screens.loadingBlock}>
          <Spinner />
        </div>
      )}

      {status === 'ready' && data?.meetsFloor && (
        <>
          {/* The percentage and the graded total are both in the widget above,
              so these tiles show the split behind them rather than repeating.
              Below the floor they are withheld — correct/incorrect would let
              anyone recompute the percentage we are deliberately not showing. */}
          <div className={styles.stats2}>
            <StatTile value={data.correct} label={t('history_correct')} />
            <StatTile value={data.incorrect} label={t('outcome_missed')} />
          </div>

          {data.trend?.hasEnoughPoints && (
            <Card className={screens.blockGap}>
              <div className={styles.chartLabel}>{t('track_over_time')}</div>
              <Sparkline points={data.trend.points} height={160} gridLines />
            </Card>
          )}

          {bySport.length > 0 && (
            <>
              <h3 className={styles.subhead}>{t('track_by_sport')}</h3>
              {/* by_sport is always all-time, whichever window is selected above. */}
              <p className={styles.tableNote}>{t('track_by_sport_all_time')}</p>
              <div className={styles.table}>
                {bySport.map((row) => (
                  <div key={row.key} className={styles.tableRow}>
                    <span className={styles.sportName}>
                      <span aria-hidden="true">{row.emoji}</span>{' '}
                      {row.name}
                    </span>

                    {/* Same floor logic as the headline figure, per row: a
                        percentage off a handful of picks is not a record. */}
                    {row.meetsFloor ? (
                      <>
                        <span className={styles.bar}>
                          <i style={{ width: `${Math.max(0, Math.min(100, row.pct))}%` }} />
                        </span>
                        <span className={`${styles.pct} mono-num`}>{row.pct}%</span>
                      </>
                    ) : (
                      <span className={styles.tooFew}>
                        {t('accuracy_graded_so_far', { graded: row.graded })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
